import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Plus, Trash2, Truck, RefreshCw } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import LoginForm from '../auth/LoginForm';
import { PhotoRow } from '../components/driver/JobInfo';
import { STATUS_STYLE } from '../components/driver/DriverReports';

const TYPES = [['', 'All'], ['defect', 'Vehicle defects'], ['expense', 'Expenses'], ['issue', 'Issues']];
const NEXT_ACTIONS = {
  open: [['resolved', 'Mark resolved']],
  resolved: [['open', 'Re-open']],
  pending: [['approved', 'Approve'], ['rejected', 'Reject']],
  approved: [['reimbursed', 'Mark reimbursed'], ['pending', 'Back to pending']],
  rejected: [['pending', 'Back to pending']],
  reimbursed: [['approved', 'Undo reimbursed']],
};

const FuelDefault = ({ authFetch }) => {
  const [f, setF] = useState(null);
  useEffect(() => { authFetch('get', '/admin/settings/fuel').then(({ data }) => setF(data)).catch(() => {}); }, [authFetch]);
  if (!f) return null;
  const save = async (e) => {
    const n = parseFloat(e.target.value);
    if (!n || n === f.default_mpg) return;
    try { const { data } = await authFetch('put', '/admin/settings/fuel', { default_mpg: n }); setF(data); toast.success('Default mpg saved.'); } catch { toast.error("Couldn't save."); }
  };
  const saveYard = async (e) => {
    const v = e.target.value.trim().toUpperCase();
    if (!v || v === f.yard_postcode) return;
    try { const { data } = await authFetch('put', '/admin/settings/fuel', { yard_postcode: v }); setF(data); toast.success('Yard postcode saved.'); } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save."); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600 rounded-md bg-slate-50 border border-slate-200 px-3 py-2" data-testid="fuel-default">
      <span>Default mpg (used when a van has no mpg or no van is on the rota)</span>
      <input type="number" min="1" max="100" step="0.1" defaultValue={f.default_mpg} onBlur={save} className="h-8 w-16 rounded border border-slate-200 px-2 text-sm" data-testid="fuel-default-mpg" />
      <span className="text-slate-400">Fuel price: UK average £{f.price_per_litre.toFixed(2)}/litre</span>
      <span className="ml-2">Yard postcode (vans start and finish here)</span>
      <input defaultValue={f.yard_postcode} maxLength={10} onBlur={saveYard} className="h-8 w-24 rounded border border-slate-200 px-2 text-sm uppercase" data-testid="fuel-yard-postcode" />
    </div>
  );
};

const VanManager = ({ authFetch, onChanged }) => {
  const [vans, setVans] = useState([]);
  const [reg, setReg] = useState('');
  const [name, setName] = useState('');
  const load = useCallback(() => authFetch('get', '/vans').then(({ data }) => setVans(data.vans)).catch(() => {}), [authFetch]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); onChanged?.(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };
  const add = (e) => {
    e.preventDefault();
    if (!reg.trim()) return;
    act(() => authFetch('post', '/admin/vans', { reg, name }), 'Van added.').then(() => { setReg(''); setName(''); });
  };
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 mb-6" data-testid="van-manager">
      <h2 className="font-display text-lg font-extrabold text-[color:var(--mv-navy)] flex items-center gap-2"><Truck className="w-5 h-5 text-[color:var(--mv-orange)]" /> Vans</h2>
      <form onSubmit={add} className="mt-3 flex flex-wrap gap-2">
        <input value={reg} onChange={(e) => setReg(e.target.value.toUpperCase())} placeholder="Reg e.g. AB12 CDE" className="h-10 rounded-md border border-slate-200 px-3 text-sm w-40" data-testid="van-reg-input" />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name e.g. Luton Van 1" className="h-10 rounded-md border border-slate-200 px-3 text-sm flex-1 min-w-[160px]" data-testid="van-name-input" />
        <button type="submit" className="h-10 px-4 rounded-md bg-[color:var(--mv-navy)] text-white text-sm font-bold inline-flex items-center gap-1" data-testid="van-add-btn"><Plus className="w-4 h-4" /> Add van</button>
      </form>
      <FuelDefault authFetch={authFetch} />
      <div className="mt-3 divide-y divide-slate-100">
        {vans.length === 0 && <div className="py-2 text-xs text-slate-500">No vans yet — add your fleet so drivers can pick them.</div>}
        {vans.map(v => (
          <div key={v.id} className="py-2 flex items-center justify-between gap-2 flex-wrap" data-testid={`van-row-${v.reg.replace(/\s/g, '')}`}>
            <div className="min-w-0">
              <div className="font-bold text-[color:var(--mv-navy)]">{v.reg} {v.name && <span className="text-slate-500 font-normal">· {v.name}</span>}</div>
              {v.off_road && <div className="text-xs text-red-700">{v.off_road_reason}</div>}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500 inline-flex items-center gap-1">mpg
                <input type="number" min="0" max="100" step="0.1" defaultValue={v.mpg ?? ''} placeholder="—" onBlur={(e) => { const n = parseFloat(e.target.value) || 0; if (n !== (v.mpg || 0)) act(() => authFetch('patch', `/admin/vans/${v.id}`, { mpg: n }), 'Van mpg saved.'); }} className="h-9 w-16 rounded-md border border-slate-200 px-2 text-sm" data-testid={`van-mpg-${v.reg.replace(/\s/g, '')}`} />
              </label>
              <button type="button" onClick={() => act(() => authFetch('patch', `/admin/vans/${v.id}`, { off_road: !v.off_road }), v.off_road ? 'Van back on the road.' : 'Van marked off road.')} className={`h-9 px-3 rounded-md text-xs font-black ${v.off_road ? 'bg-red-600 text-white' : 'bg-emerald-100 text-emerald-800'}`} data-testid={`van-offroad-toggle-${v.reg.replace(/\s/g, '')}`}>
                {v.off_road ? 'OFF ROAD · tap to clear' : 'On the road'}
              </button>
              <button type="button" onClick={() => window.confirm(`Delete van ${v.reg}?`) && act(() => authFetch('delete', `/admin/vans/${v.id}`), 'Van deleted.')} className="h-9 w-9 inline-flex items-center justify-center text-slate-400 hover:text-red-600" aria-label="Delete van" data-testid={`van-delete-${v.reg.replace(/\s/g, '')}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const ReportCard = ({ r, authFetch, onChanged }) => {
  const [note, setNote] = useState(r.admin_note);
  const update = async (body, msg) => {
    try { await authFetch('patch', `/admin/reports/${r.id}`, body); toast.success(msg); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-report-${r.ref}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="font-bold text-[color:var(--mv-navy)]">{r.category_label}{r.amount ? ` · £${r.amount.toFixed(2)}` : ''}</div>
          <div className="text-xs text-slate-500">#{r.ref} · {r.driver_name}{r.van_reg ? ` · ${r.van_reg}` : ''}{r.job_ref ? ` · job #${r.job_ref}` : ''} · {new Date(r.created_at + 'Z').toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
          {r.van_marked_off_road && <div className="text-[11px] font-bold text-red-700 mt-0.5">Van was marked off road</div>}
        </div>
        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status]}`} data-testid={`admin-report-status-${r.ref}`}>{r.status}</span>
      </div>
      {r.description && <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{r.description}</p>}
      {r.photos.length > 0 && <div className="mt-2"><PhotoRow photos={r.photos} testid={`admin-report-photos-${r.ref}`} /></div>}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {(NEXT_ACTIONS[r.status] || []).map(([st, label]) => (
          <button key={st} type="button" onClick={() => update({ status: st }, `${label} · #${r.ref}`)} className="h-9 px-3 rounded-md bg-[color:var(--mv-navy)] text-white text-xs font-bold" data-testid={`admin-report-action-${st}-${r.ref}`}>{label}</button>
        ))}
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for the driver" className="h-9 flex-1 min-w-[160px] rounded-md border border-slate-200 px-2 text-xs" data-testid={`admin-report-note-${r.ref}`} />
        {note !== r.admin_note && <button type="button" onClick={() => update({ admin_note: note }, 'Note saved.')} className="h-9 px-3 rounded-md border border-slate-200 text-xs font-bold" data-testid={`admin-report-note-save-${r.ref}`}>Save note</button>}
      </div>
    </div>
  );
};

function ReportsInner() {
  const { authFetch } = useAuth();
  const [type, setType] = useState('');
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    authFetch('get', `/admin/reports${type ? `?type=${type}` : ''}`).then(({ data }) => setData(data)).catch(() => toast.error("Couldn't load reports."));
  }, [authFetch, type]);
  useEffect(() => { load(); }, [load]);

  const c = data?.counts || {};
  return (
    <div className="min-h-[60vh] bg-slate-50 py-6 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-5">
          <Link to="/admin" className="h-9 w-9 rounded-md border border-slate-200 bg-white inline-flex items-center justify-center" title="Back to dashboard"><ArrowLeft className="w-4 h-4 text-slate-600" /></Link>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.22em] font-black text-[color:var(--mv-orange)]">Fleet & staff</div>
            <h1 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)]">Driver reports</h1>
          </div>
          <button onClick={load} className="h-9 w-9 rounded-md border border-slate-200 bg-white inline-flex items-center justify-center" title="Refresh"><RefreshCw className="w-4 h-4 text-slate-600" /></button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[['Open defects', c.open_defects, 'reports-count-defects'], ['Open issues', c.open_issues, 'reports-count-issues'], ['Expenses to approve', c.pending_expenses, 'reports-count-expenses'], ['Vans off road', c.vans_off_road, 'reports-count-offroad']].map(([l, v, t]) => (
            <div key={t} className="rounded-xl bg-white border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{l}</div>
              <div className="font-display text-3xl font-black text-[color:var(--mv-navy)]" data-testid={t}>{v ?? '—'}</div>
            </div>
          ))}
        </div>
        <VanManager authFetch={authFetch} onChanged={load} />
        <div className="flex flex-wrap gap-2 mb-3">
          {TYPES.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setType(k)} className={`h-9 px-3 rounded-full text-xs font-bold border ${type === k ? 'bg-[color:var(--mv-navy)] text-white border-[color:var(--mv-navy)]' : 'bg-white text-slate-600 border-slate-200'}`} data-testid={`reports-filter-${k || 'all'}`}>{l}</button>
          ))}
        </div>
        {!data ? <Loader2 className="w-5 h-5 animate-spin text-[color:var(--mv-orange)]" /> : (
          <div className="space-y-3" data-testid="admin-reports-list">
            {data.reports.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500">No reports here.</div>}
            {data.reports.map(r => <ReportCard key={`${r.id}-${r.status}-${r.admin_note}`} r={r} authFetch={authFetch} onChanged={load} />)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminReports() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!user) return <LoginForm title="Move Van Ltd · Owner" subtitle="Sign in to view driver reports." expectedRole="owner" />;
  if (user.role !== 'owner') return <div className="min-h-[60vh] flex items-center justify-center text-slate-600">Owners only.</div>;
  return <ReportsInner />;
}
